class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

(() => {
  if (window.ScrollLock) return;

  // Shared scroll lock helper used by drawers/modals
  let lockCount = 0;
  let backgroundLockCount = 0;
  let restoreScrollLockCount = 0;
  let previousPaddingRight = '';
  let hasSavedScrollPosition = false;

  function lock({ background = false, restoreScroll = true } = {}) {
    const html = document.documentElement;
    const body = document.body;

    if (!html || !body) return;

    if (lockCount === 0) {
      previousPaddingRight = body.style.paddingRight;

      const scrollbarWidth = window.innerWidth - html.clientWidth;
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    if (restoreScroll) {
      restoreScrollLockCount += 1;

      if (!hasSavedScrollPosition) {
        const scrollY = window.scrollY || window.pageYOffset || 0;
        body.dataset.scrollLockY = scrollY;
        body.style.setProperty('--scroll-lock-top', `-${scrollY}px`);
        hasSavedScrollPosition = true;
      }
    }

    lockCount += 1;
    body.classList.add('no-scroll');

    if (background) {
      backgroundLockCount += 1;
      body.classList.add('no-scroll-bg');
    }
  }

  function unlock({ background = false, restoreScroll = true } = {}) {
    const body = document.body;

    if (!body || lockCount === 0) return;

    lockCount -= 1;

    if (background && backgroundLockCount > 0) {
      backgroundLockCount -= 1;
      if (backgroundLockCount === 0) {
        body.classList.remove('no-scroll-bg');
      }
    }

    if (restoreScroll && restoreScrollLockCount > 0) {
      restoreScrollLockCount -= 1;
    }

    if (lockCount > 0) return;

    body.classList.remove('no-scroll');

    if (backgroundLockCount === 0) {
      body.classList.remove('no-scroll-bg');
    }

    if (hasSavedScrollPosition) {
      const scrollY = parseInt(body.dataset.scrollLockY || '0', 10);
      body.style.removeProperty('--scroll-lock-top');
      delete body.dataset.scrollLockY;
      window.scrollTo(0, scrollY);
      hasSavedScrollPosition = false;
      restoreScrollLockCount = 0;
    }

    body.style.paddingRight = previousPaddingRight;
    previousPaddingRight = '';
  }

  window.ScrollLock = { lock, unlock };
})();
